/**
 * JobMockAI — Community Feed Page
 *
 * Route: /community/feed
 * Interview-focused peer feed — share practice wins, tips & feedback requests.
 * Supports: create post, like/unlike, comment thread, author profile navigation.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  IconButton,
  Collapse,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Container,
  LinearProgress,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Send,
  FavoriteBorder,
  Favorite,
  ChatBubbleOutline,
  EmojiEvents,
  RateReview,
  MoreVert,
  EditOutlined,
  DeleteOutline,
  PersonSearch,
  ImageOutlined,
  PhotoCamera,
  VideocamOutlined,
  FiberManualRecord,
  Stop,
  Tune,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { resolveMediaUrl } from '../utils/resolveMediaUrl';
import { compressImageFile } from '../utils/compressImageFile';
import { applyImageEdits } from '../utils/applyImageEdits';
import { applyVideoEdits } from '../utils/applyVideoEdits';
import { buildColorCssFilter, hasColorEdits } from '../utils/mediaColorFilter';
import { readVideoDuration } from '../utils/readVideoDuration';
import {
  COMMUNITY_VIDEO_CAPTURE_CONSTRAINTS,
  createCappedMediaRecorder,
  maybeCompressCommunityVideo,
} from '../utils/communityVideoCapture';
import PostImageEditor from '../components/community/PostImageEditor';
import PostVideoEditor from '../components/community/PostVideoEditor';
import FeedPostVideo from '../components/community/FeedPostVideo';
import CommunityRightRail from '../components/community/CommunityRightRail';
import {
  buildOptimisticCommunityPost,
  enqueueCommunityPost,
  retryCommunityUpload,
  dismissCommunityUpload,
  subscribeCommunityUploads,
} from '../services/communityPostUploadQueue';

const POST_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const POST_VIDEO_MAX_BYTES_DEFAULT = 40 * 1024 * 1024;
const POST_VIDEO_MAX_SECONDS_DEFAULT = 60;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const ALLOWED_VIDEO_TYPES = new Set(['video/webm', 'video/mp4', 'video/quicktime']);

function formatMbLabel(bytes) {
  const mb = Number(bytes) / (1024 * 1024);
  if (mb >= 10) return `${Math.round(mb)} MB`;
  return `${Math.round(mb * 10) / 10} MB`;
}

const panelSx = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
};

/** Matches AppShell `shellContentAreaSx` xs horizontal gutter (theme spacing units). */
const SHELL_GUTTER_XS = 1.5;

/**
 * LinkedIn/Meta-style posts:
 * - Media is always edge-to-edge of the post card (no side inset / letterboxing)
 * - Header / body / actions keep horizontal padding
 * - xs: flush strips; sm+: light card chrome without padding around media
 */
const postCardSx = {
  bgcolor: 'background.paper',
  borderColor: 'divider',
  overflow: 'hidden',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  mx: 0,
  px: 0, // critical — never pad the card itself or media will inset
  pl: 0,
  pr: 0,
  mb: { xs: 0, sm: 2 },
  borderRadius: { xs: 0, sm: 2 },
  borderStyle: 'solid',
  borderWidth: { xs: 0, sm: 1 },
  borderBottomWidth: 1,
  boxShadow: {
    xs: 'none',
    sm: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  transition: 'border-color 0.15s, box-shadow 0.15s',
  '@media (min-width:600px)': {
    '&:hover': {
      borderColor: 'grey.400',
      boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
    },
  },
};

// ── Helpers ───────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getInitials(name = '') {
  return (
    name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?'
  );
}

// ── Comment item ──────────────────────────────────────────────────
function CommentItem({ comment: initialComment, postId, currentUser, onAuthorClick, onDeleted }) {
  const { user } = initialComment;
  const isOwner = currentUser && user?.id && String(currentUser.id) === String(user.id);

  const [comment, setComment] = useState(initialComment);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed || trimmed === comment.content) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.community.updateComment(postId, comment.id, { content: trimmed });
      setComment((c) => ({ ...c, content: trimmed }));
      setEditing(false);
    } catch {
      // silently ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.community.deleteComment(postId, comment.id);
      setConfirmDelete(false);
      onDeleted(comment.id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ py: 0.75 }}>
        <Tooltip title={user?.name || 'User'}>
          <Avatar
            src={user?.avatar_url || undefined}
            alt={user?.name}
            sx={{ width: 28, height: 28, bgcolor: 'secondary.light', cursor: 'pointer', flexShrink: 0, mt: 0.25 }}
            onClick={() => user?.id && onAuthorClick(user.id)}
          >
            {getInitials(user?.name)}
          </Avatar>
        </Tooltip>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => user?.id && onAuthorClick(user.id)}
            >
              {user?.name || 'Unknown'}
            </Typography>
            <Typography variant="caption" color="text.disabled">
              {timeAgo(comment.created_at)}
            </Typography>
          </Stack>
          {editing ? (
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <TextField
                size="small"
                fullWidth
                multiline
                maxRows={4}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                disabled={saving}
                autoFocus
              />
              <Button size="small" variant="contained" onClick={handleSaveEdit} disabled={saving || !editText.trim()} sx={{ alignSelf: 'flex-start', minWidth: 0, px: 1.5 }}>
                {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
              </Button>
              <Button size="small" onClick={() => { setEditing(false); setEditText(comment.content); }} disabled={saving} sx={{ alignSelf: 'flex-start', minWidth: 0 }}>
                Cancel
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {comment.content}
            </Typography>
          )}
        </Box>
        {isOwner && !editing && (
          <>
            <IconButton size="small" sx={{ alignSelf: 'flex-start', mt: 0.25 }} onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="Comment options">
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
              <MenuItem onClick={() => { setMenuAnchor(null); setEditing(true); setEditText(comment.content); }}>
                <EditOutlined fontSize="small" sx={{ mr: 1 }} /> Edit
              </MenuItem>
              <MenuItem onClick={() => { setMenuAnchor(null); setConfirmDelete(true); }} sx={{ color: 'error.main' }}>
                <DeleteOutline fontSize="small" sx={{ mr: 1 }} /> Delete
              </MenuItem>
            </Menu>
          </>
        )}
      </Stack>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete comment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting} variant="contained">
            {deleting ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Add comment form ──────────────────────────────────────────────
function AddCommentForm({ postId, currentUser, onCommentAdded }) {
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const res = await api.community.createComment(postId, { content: trimmed });
      onCommentAdded(res.data.comment);
      setText('');
    } catch {
      // silently ignore — comment is secondary UX
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
      <Avatar
        src={currentUser?.avatar_url || undefined}
        alt={currentUser?.name || 'You'}
        sx={{ width: 28, height: 28, bgcolor: 'primary.main', flexShrink: 0, mt: 0.5 }}
      >
        {getInitials(currentUser?.name || currentUser?.first_name)}
      </Avatar>
      <Box component="form" onSubmit={handleSubmit} sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Write a comment…"
    id={`comment-input-${postId || 'new'}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ maxLength: 1000 }}
          disabled={submitting}
          multiline
          maxRows={4}
        />
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={!text.trim() || submitting}
          sx={{ minWidth: 0, px: 1.5, alignSelf: 'flex-start' }}
          aria-label="Post comment"
        >
          {submitting ? <CircularProgress size={14} color="inherit" /> : <Send fontSize="small" />}
        </Button>
      </Box>
    </Stack>
  );
}

// ── Post card ─────────────────────────────────────────────────────
function PostCard({ post: initialPost, currentUser, onAuthorClick, onPostDeleted, compact = false }) {
  const [post, setPost] = useState(initialPost);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [likingInProgress, setLikingInProgress] = useState(false);

  // Post edit/delete state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.content);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isOwner = Boolean(
    currentUser?.id &&
      (String(currentUser.id) === String(post.user?.id || '') ||
        String(currentUser.id) === String(post.user_id || ''))
  );
  const isTempPost = String(post.id || '').startsWith('pending-');

  useEffect(() => { setPost(initialPost); }, [initialPost]);

  const handleLike = async () => {
    if (likingInProgress) return;
    setLikingInProgress(true);
    // Optimistic update
    setPost((p) => ({
      ...p,
      liked_by_me: !p.liked_by_me,
      like_count: p.liked_by_me ? Math.max((p.like_count || 1) - 1, 0) : (p.like_count || 0) + 1,
    }));
    try {
      const res = await api.community.likePost(post.id);
      setPost((p) => ({ ...p, liked_by_me: res.data.liked, like_count: res.data.like_count }));
    } catch {
      // Revert on error
      setPost((p) => ({
        ...p,
        liked_by_me: !p.liked_by_me,
        like_count: p.liked_by_me ? Math.max((p.like_count || 1) - 1, 0) : (p.like_count || 0) + 1,
      }));
    } finally {
      setLikingInProgress(false);
    }
  };

  const handleToggleComments = async () => {
    const opening = !showComments;
    setShowComments(opening);
    if (opening && !commentsLoaded) {
      setLoadingComments(true);
      try {
        const res = await api.community.getComments(post.id);
        setComments(res.data.comments || []);
        setCommentsLoaded(true);
      } catch {
        // ignore
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const handleCommentAdded = (newComment) => {
    setComments((prev) => [...prev, newComment]);
    setPost((p) => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
  };

  const handleCommentDeleted = (commentId) => {
    setComments((prev) => prev.filter((c) => c.id !== commentId));
    setPost((p) => ({ ...p, comment_count: Math.max((p.comment_count || 1) - 1, 0) }));
  };

  const handleSaveEdit = async () => {
    const trimmed = editText.trim();
    if (trimmed === (post.content || '')) { setEditing(false); return; }
    if (!trimmed && !(post.image_url || post.image_key || post.video_url || post.video_key)) return;
    setSavingEdit(true);
    try {
      const res = await api.community.updatePost(post.id, { content: trimmed });
      setPost((p) => ({
        ...p,
        content: trimmed,
        ...(res.data?.post || {}),
      }));
      setEditing(false);
    } catch {
      // silently ignore
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      if (isTempPost) {
        if (uploadMeta?.tempId) dismissCommunityUpload(uploadMeta.tempId);
        setConfirmDelete(false);
        onPostDeleted(post.id);
        return;
      }
      await api.community.deletePost(post.id);
      setConfirmDelete(false);
      onPostDeleted(post.id);
    } catch {
      setDeleting(false);
    }
  };

  const { user } = post;
  const imageSrc = resolveMediaUrl(post.image_url);
  const videoSrc = resolveMediaUrl(post.video_url);
  const uploadMeta = post._upload || null;
  const isUploading = uploadMeta && ['queued', 'preparing', 'uploading', 'posting'].includes(uploadMeta.status);
  const uploadFailed = uploadMeta?.status === 'error';
  const moderationStatus = post.media_moderation_status || null;
  const hasMedia = Boolean(post.image_key || post.video_key || imageSrc || videoSrc);
  const showPending = hasMedia && moderationStatus === 'pending_review';
  const showRejected = hasMedia && moderationStatus === 'rejected';
  // Keep owner actions available after create; only hide while the temp upload card is mid-flight.
  const showOwnerActions = isOwner && !editing && !(isTempPost && isUploading);

  return (
    <Box
      sx={{
        ...postCardSx,
        // Dashboard glimpse: strip chrome so media can meet the column edges
        ...(compact
          ? {
              borderRadius: 0,
              borderLeftWidth: 0,
              borderRightWidth: 0,
              boxShadow: 'none',
              mb: 0,
            }
          : null),
        opacity: isUploading ? 0.92 : 1,
      }}
      data-testid={
        isUploading
          ? 'post-uploading'
          : uploadFailed
            ? 'post-upload-failed'
            : 'community-post-card'
      }
    >
      {isUploading && (
        <LinearProgress
          variant={uploadMeta.status === 'uploading' ? 'determinate' : 'indeterminate'}
          value={uploadMeta.progress || 0}
          data-testid="post-card-upload-progress"
        />
      )}
      {uploadFailed && (
        <Alert
          severity="error"
          sx={{ borderRadius: 0 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => retryCommunityUpload(uploadMeta.tempId)}
              sx={{ textTransform: 'none' }}
            >
              Retry
            </Button>
          }
        >
          {uploadMeta.error || 'Upload failed'}
        </Alert>
      )}
      {isUploading && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2.25, pt: 1, display: 'block' }}>
          Uploading in the background — feel free to leave this page.
        </Typography>
      )}
      {showPending && !isUploading && (
        <Alert severity="info" sx={{ borderRadius: 0 }} data-testid="post-pending-approval">
          Pending administrator approval — only you can see this until it is reviewed.
        </Alert>
      )}
      {showRejected && (
        <Alert severity="warning" sx={{ borderRadius: 0 }} data-testid="post-rejected">
          Rejected{post.media_moderation_notes ? `: ${post.media_moderation_notes}` : '.'} You can
          upload a replacement.
        </Alert>
      )}
      <Box sx={{ px: { xs: 1.5, sm: 2.25 }, pt: { xs: 1.5, sm: 2 }, pb: 1.25 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Tooltip title={`View ${user?.name || 'profile'}`}>
            <Avatar
              src={resolveMediaUrl(user?.avatar_url) || undefined}
              alt={user?.name || 'User'}
              sx={{ width: 42, height: 42, bgcolor: 'grey.200', color: 'text.primary', flexShrink: 0, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}
              onClick={() => user?.id && onAuthorClick(user.id)}
            >
              {getInitials(user?.name)}
            </Avatar>
          </Tooltip>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => user?.id && onAuthorClick(user.id)}
              >
                {user?.name || 'Unknown user'}
              </Typography>
              {showPending && (
                <Chip size="small" label="Pending Approval" color="warning" data-testid="post-moderation-badge" />
              )}
              {showRejected && (
                <Chip size="small" label="Rejected" color="error" data-testid="post-moderation-badge" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.15 }}>
              {timeAgo(post.created_at)}
            </Typography>
          </Box>

          {showOwnerActions && (
            <IconButton
              size="small"
              sx={{ flexShrink: 0, mt: -0.5 }}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="Post options"
              data-testid="post-owner-menu"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          )}
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setEditing(true);
                setEditText(post.content || '');
              }}
              disabled={isTempPost}
            >
              <EditOutlined fontSize="small" sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setConfirmDelete(true);
              }}
              sx={{ color: 'error.main' }}
              data-testid="post-delete-menu-item"
            >
              <DeleteOutline fontSize="small" sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>
        </Stack>

        {/* Body spans full content width (not indented under avatar) — LinkedIn-style on mobile. */}
        {editing ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              maxRows={8}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              disabled={savingEdit}
              autoFocus
              data-testid="post-body-edit"
            />
            <Stack spacing={0.5} sx={{ flexShrink: 0 }}>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveEdit}
                disabled={savingEdit || (!editText.trim() && !(post.image_url || post.image_key || post.video_url || post.video_key))}
                sx={{ minWidth: 0, px: 1.5, textTransform: 'none' }}
              >
                {savingEdit ? <CircularProgress size={14} color="inherit" /> : 'Save'}
              </Button>
              <Button size="small" onClick={() => { setEditing(false); setEditText(post.content || ''); }} disabled={savingEdit} sx={{ minWidth: 0, textTransform: 'none' }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        ) : (
          post.content ? (
            <Typography
              variant="body2"
              data-testid="post-body-text"
              sx={{
                mt: 1.25,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.55,
                color: 'text.primary',
                width: '100%',
              }}
            >
              {post.content}
            </Typography>
          ) : null
        )}
      </Box>

      {imageSrc && (
        <Box
          data-testid="post-media-wrap"
          data-full-bleed="true"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            m: 0,
            p: 0,
            lineHeight: 0,
            fontSize: 0,
            overflow: 'hidden',
            bgcolor: 'common.black',
          }}
        >
          <Box
            component="img"
            src={imageSrc}
            alt=""
            loading="lazy"
            data-testid={compact ? 'post-image-constrained' : 'post-image'}
            sx={{
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              // Never letterbox — width drives size; no maxHeight / object-fit contain
              maxHeight: 'none',
              m: 0,
              p: 0,
              border: 0,
              verticalAlign: 'top',
            }}
          />
        </Box>
      )}

      {videoSrc && (
        <Box
          data-testid="post-media-wrap"
          data-full-bleed="true"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            m: 0,
            p: 0,
            lineHeight: 0,
            fontSize: 0,
            overflow: 'hidden',
            bgcolor: 'common.black',
          }}
        >
          <FeedPostVideo
            src={videoSrc}
            compact={compact}
            data-testid={compact ? 'post-video-constrained' : 'post-video'}
            style={{
              display: 'block',
              width: '100%',
              maxWidth: '100%',
              height: 'auto',
              margin: 0,
              padding: 0,
              border: 0,
              backgroundColor: '#000',
            }}
          />
        </Box>
      )}

      <Stack direction="row" spacing={0.5} sx={{ px: { xs: 1.5, sm: 2.25 }, py: { xs: 1, sm: 1.25 } }} alignItems="center">
        <Tooltip title={post.liked_by_me ? 'Unlike' : 'Like'}>
          <span>
            <IconButton
              onClick={handleLike}
              disabled={likingInProgress || isUploading || uploadFailed || isTempPost}
              color={post.liked_by_me ? 'error' : 'default'}
              aria-label={post.liked_by_me ? 'Unlike post' : 'Like post'}
              sx={{ width: 44, height: 44 }}
            >
              {post.liked_by_me ? <Favorite /> : <FavoriteBorder />}
            </IconButton>
          </span>
        </Tooltip>
        {post.like_count > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }} fontWeight={600}>
            {post.like_count}
          </Typography>
        )}

        <Tooltip title={showComments ? 'Hide comments' : 'Comment'}>
          <span>
            <IconButton
              onClick={handleToggleComments}
              disabled={isTempPost}
              aria-label="Toggle comments"
              sx={{ width: 44, height: 44 }}
            >
              <ChatBubbleOutline />
            </IconButton>
          </span>
        </Tooltip>
        {(post.comment_count > 0 || comments.length > 0) && (
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {Math.max(post.comment_count || 0, comments.length)}
          </Typography>
        )}

        {showOwnerActions && !isTempPost && (
          <Tooltip title="Delete post">
            <IconButton
              aria-label="Delete post"
              onClick={() => setConfirmDelete(true)}
              data-testid="post-delete-button"
              sx={{
                ml: 'auto',
                color: 'text.secondary',
                width: 44,
                height: 44,
                // Prefer ⋯ menu on phones to avoid accidental deletes next to Like.
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Collapse in={showComments}>
        <Box sx={{ px: { xs: 1.5, sm: 2.25 }, pb: 2, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {loadingComments && (
            <CircularProgress size={18} sx={{ display: 'block', mx: 'auto', my: 1 }} />
          )}
          {!loadingComments && commentsLoaded && comments.length === 0 && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
              No comments yet. Be the first!
            </Typography>
          )}
          {comments.map((c) => (
            <CommentItem key={c.id} comment={c} postId={post.id} currentUser={currentUser} onAuthorClick={onAuthorClick} onDeleted={handleCommentDeleted} />
          ))}
          <Divider sx={{ my: 1 }} />
          <AddCommentForm postId={post.id} currentUser={currentUser} onCommentAdded={handleCommentAdded} />
        </Box>
      </Collapse>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete post?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This will permanently delete the post and all its comments. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeletePost} color="error" disabled={deleting} variant="contained">
            {deleting ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Create post form ──────────────────────────────────────────────
function CreatePostForm({ onPostCreated, currentUser, initialContent = '' }) {
  const [content, setContent] = useState(() => initialContent || '');
  const [error, setError] = useState('');
  const [moderationNotice, setModerationNotice] = useState('');
  const [videoMaxBytes, setVideoMaxBytes] = useState(POST_VIDEO_MAX_BYTES_DEFAULT);
  const [videoMaxSeconds, setVideoMaxSeconds] = useState(POST_VIDEO_MAX_SECONDS_DEFAULT);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [videoDuration, setVideoDuration] = useState(null);
  const [optimizingImage, setOptimizingImage] = useState(false);
  const [optimizingVideo, setOptimizingVideo] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSource, setEditorSource] = useState(null);
  const [editorSourceUrl, setEditorSourceUrl] = useState('');
  const [editorApplying, setEditorApplying] = useState(false);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [videoEditorSource, setVideoEditorSource] = useState(null);
  const [videoEditorSourceUrl, setVideoEditorSourceUrl] = useState('');
  const [videoEditorDuration, setVideoEditorDuration] = useState(null);
  const [videoPreviewFilter, setVideoPreviewFilter] = useState('');
  const videoBakePromiseRef = useRef(null);
  const videoFileRef = useRef(null);
  const videoPreviewRef = useRef('');
  // When Post hands preview blob URLs to the upload queue, skip the next effect revoke
  // so the optimistic feed card can keep showing media until the queue cleans up.
  const skipNextImagePreviewRevokeRef = useRef(false);
  const skipNextVideoPreviewRevokeRef = useRef(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [videoRecordOpen, setVideoRecordOpen] = useState(false);
  const [videoRecordError, setVideoRecordError] = useState('');
  const [videoRecordStarting, setVideoRecordStarting] = useState(false);
  const [videoRecordReady, setVideoRecordReady] = useState(false);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoRecordElapsed, setVideoRecordElapsed] = useState(0);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const videoRecordRef = useRef(null);
  const videoRecordStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStartedAtRef = useRef(0);

  useEffect(() => () => {
    if (!imagePreview) return;
    if (skipNextImagePreviewRevokeRef.current) {
      skipNextImagePreviewRevokeRef.current = false;
      return;
    }
    URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  useEffect(() => () => {
    if (!videoPreview) return;
    if (skipNextVideoPreviewRevokeRef.current) {
      skipNextVideoPreviewRevokeRef.current = false;
      return;
    }
    URL.revokeObjectURL(videoPreview);
  }, [videoPreview]);

  useEffect(() => () => {
    if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
  }, [editorSourceUrl]);

  useEffect(() => () => {
    if (videoEditorSourceUrl) URL.revokeObjectURL(videoEditorSourceUrl);
  }, [videoEditorSourceUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.community.getPostMediaLimits();
        if (cancelled || !data?.video) return;
        if (Number.isFinite(Number(data.video.maxBytes))) {
          setVideoMaxBytes(Number(data.video.maxBytes));
        }
        if (Number.isFinite(Number(data.video.maxDurationSeconds))) {
          setVideoMaxSeconds(Number(data.video.maxDurationSeconds));
        }
      } catch {
        // Keep defaults if limits endpoint is unavailable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stopCameraStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const stopVideoRecordTracks = useCallback((opts = {}) => {
    const { stopRecorder = true } = opts;
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (stopRecorder && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    const stream = videoRecordStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      videoRecordStreamRef.current = null;
    }
    if (videoRecordRef.current) videoRecordRef.current.srcObject = null;
    setVideoRecordReady(false);
    setVideoRecording(false);
    setVideoRecordElapsed(0);
  }, []);

  useEffect(() => () => {
    stopCameraStream();
    stopVideoRecordTracks();
  }, [stopCameraStream, stopVideoRecordTracks]);

  useEffect(() => {
    if (!cameraOpen) return undefined;
    let cancelled = false;

    const start = async () => {
      setCameraError('');
      setCameraStarting(true);
      setCameraReady(false);
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setCameraOpen(false);
          setCameraStarting(false);
          cameraInputRef.current?.click();
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setCameraReady(true);
      } catch {
        if (!cancelled) {
          setCameraError('Could not access the camera. You can still choose a photo from your library.');
        }
      } finally {
        if (!cancelled) setCameraStarting(false);
      }
    };

    start();
    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [cameraOpen, stopCameraStream]);

  const clearVideo = () => {
    if (videoBakePromiseRef.current) {
      videoBakePromiseRef.current.cancelled = true;
      videoBakePromiseRef.current = null;
    }
    setOptimizingVideo(false);
    setVideoPreviewFilter('');
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(null);
    setVideoPreview('');
    setVideoDuration(null);
    videoFileRef.current = null;
    videoPreviewRef.current = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
    setImageFile(null);
    setImagePreview('');
    setEditorSource(null);
    setEditorSourceUrl('');
    setEditorOpen(false);
    setEditorApplying(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const clearMedia = () => {
    clearImage();
    clearVideo();
  };

  const commitProcessedImage = async (file) => {
    setOptimizingImage(true);
    try {
      const optimized = await compressImageFile(file, {
        maxBytes: POST_IMAGE_MAX_BYTES,
        maxEdge: 1920,
        targetBytes: 1.5 * 1024 * 1024,
      });
      clearVideo();
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImageFile(optimized);
      setImagePreview(URL.createObjectURL(optimized));
    } catch (err) {
      setError(err.message || 'Could not process that image. Please try another.');
    } finally {
      setOptimizingImage(false);
    }
  };

  const openImageEditor = (file) => {
    if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
    setEditorSource(file);
    setEditorSourceUrl(URL.createObjectURL(file));
    setEditorOpen(true);
  };

  const beginImageFlow = (file) => {
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError('Please choose a JPEG, PNG, WebP, or GIF image.');
      return;
    }
    if (file.size > POST_IMAGE_MAX_BYTES) {
      setError('Image must be 5 MB or smaller.');
      return;
    }
    setError('');
    // Preserve animated GIFs — skip raster editor.
    if (file.type === 'image/gif') {
      commitProcessedImage(file);
      return;
    }
    openImageEditor(file);
  };

  const handleEditorClose = () => {
    if (editorApplying) return;
    setEditorOpen(false);
    if (!imageFile) {
      if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
      setEditorSource(null);
      setEditorSourceUrl('');
    }
  };

  const handleEditorApply = async (edits) => {
    if (!editorSource) return;
    setEditorApplying(true);
    setError('');
    try {
      const edited = await applyImageEdits(editorSource, edits);
      setEditorOpen(false);
      await commitProcessedImage(edited);
      if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
      setEditorSource(null);
      setEditorSourceUrl('');
    } catch (err) {
      setError(err.message || 'Could not apply photo adjustments.');
    } finally {
      setEditorApplying(false);
    }
  };

  const handlePickImage = (e) => {
    const next = e.target.files?.[0];
    if (next) beginImageFlow(next);
  };

  const commitVideoFile = (file, durationSeconds) => {
    clearImage();
    if (videoPreviewRef.current) URL.revokeObjectURL(videoPreviewRef.current);
    const url = URL.createObjectURL(file);
    videoFileRef.current = file;
    videoPreviewRef.current = url;
    setVideoFile(file);
    setVideoPreview(url);
    setVideoDuration(durationSeconds);
  };

  const openVideoEditor = (file, durationSeconds) => {
    if (videoEditorSourceUrl) URL.revokeObjectURL(videoEditorSourceUrl);
    setVideoEditorSource(file);
    setVideoEditorSourceUrl(URL.createObjectURL(file));
    setVideoEditorDuration(durationSeconds);
    setVideoEditorOpen(true);
  };

  const closeVideoEditor = () => {
    setVideoEditorOpen(false);
    // Keep source around only if we already committed a videoFile from Skip/Apply.
    if (!videoFile) {
      if (videoEditorSourceUrl) URL.revokeObjectURL(videoEditorSourceUrl);
      setVideoEditorSource(null);
      setVideoEditorSourceUrl('');
      setVideoEditorDuration(null);
    }
  };

  const beginVideoFlow = async (file, durationOverride) => {
    if (!file) return;
    const baseType = String(file.type || '').split(';')[0].trim().toLowerCase();
    const typeOk =
      ALLOWED_VIDEO_TYPES.has(baseType) ||
      baseType === 'video/webm' ||
      baseType === 'video/mp4' ||
      baseType.startsWith('video/');
    if (!typeOk) {
      setError('Please choose an MP4, WebM, or MOV video.');
      return;
    }
    setError('');
    try {
      let duration = Number(durationOverride);
      if (!Number.isFinite(duration) || duration <= 0) {
        duration = await readVideoDuration(file, {
          fallbackSeconds: null,
          timeoutMs: 6000,
        });
      }
      if (duration > videoMaxSeconds + 0.5) {
        setError(
          `Please use a video of ${videoMaxSeconds} seconds or less (yours is ${duration.toFixed(1)}s).`
        );
        return;
      }

      let working = file;
      if (file.size > videoMaxBytes) {
        setOptimizingVideo(true);
        try {
          const result = await maybeCompressCommunityVideo(file, {
            maxBytes: videoMaxBytes,
            maxSeconds: videoMaxSeconds,
            durationHint: duration,
          });
          working = result.file;
          if (working.size > videoMaxBytes) {
            setError(
              `Video is still over ${formatMbLabel(videoMaxBytes)} after compression. Try a shorter clip or lower camera quality.`
            );
            return;
          }
        } finally {
          setOptimizingVideo(false);
        }
      }

      openVideoEditor(working, Math.min(videoMaxSeconds, Math.max(0.5, duration)));
    } catch (err) {
      setOptimizingVideo(false);
      setError(err.message || 'Could not read that video.');
    }
  };

  const handleVideoEditorApply = (edits) => {
    if (!videoEditorSource) return;
    const source = videoEditorSource;
    const duration = videoEditorDuration;
    const editorUrl = videoEditorSourceUrl;
    const brightness = Number(edits?.brightness) || 0;
    const contrast = Number(edits?.contrast) || 0;
    const saturation = Number(edits?.saturation) || 0;
    const warmth = Number(edits?.warmth) || 0;
    const colorEdits = { brightness, contrast, saturation, warmth };
    const needsBake = hasColorEdits(colorEdits);

    // Close dialog immediately — bake in the background.
    setVideoEditorOpen(false);
    setError('');
    commitVideoFile(source, duration);
    setVideoPreviewFilter(needsBake ? buildColorCssFilter(colorEdits) : '');

    // Detach editor state (preview URL now owned by videoPreview via commitVideoFile).
    setVideoEditorSource(null);
    setVideoEditorSourceUrl('');
    setVideoEditorDuration(null);
    if (editorUrl) URL.revokeObjectURL(editorUrl);

    if (!needsBake) {
      videoBakePromiseRef.current = null;
      setOptimizingVideo(false);
      return;
    }

    setOptimizingVideo(true);
    const bakePromise = applyVideoEdits(source, colorEdits, {
      maxSeconds: videoMaxSeconds,
      durationHint: duration,
    })
      .then((edited) => {
        if (bakePromise.cancelled) return null;
        if (edited.size > videoMaxBytes) {
          throw new Error('Adjusted video is too large. Try milder adjustments or a shorter clip.');
        }
        // When Post already handed this bake to the upload queue, skip form updates.
        if (!bakePromise.handedOff) {
          commitVideoFile(edited, duration);
          setVideoPreviewFilter('');
        }
        return edited;
      })
      .catch((err) => {
        if (bakePromise.cancelled) return null;
        if (!bakePromise.handedOff) {
          // Keep the original clip; CSS preview is cleared so users know bake did not stick.
          setError(
            err.message ||
              'Could not bake video adjustments. You can still post — preview filters may not appear for others.'
          );
          setVideoPreviewFilter('');
        }
        return null;
      })
      .finally(() => {
        if (bakePromise.cancelled) return;
        if (!bakePromise.handedOff) {
          setOptimizingVideo(false);
        }
        if (videoBakePromiseRef.current === bakePromise) {
          videoBakePromiseRef.current = null;
        }
      });
    bakePromise.cancelled = false;
    bakePromise.handedOff = false;
    videoBakePromiseRef.current = bakePromise;
  };

  const handleVideoEditorSkip = () => {
    if (!videoEditorSource) return;
    const source = videoEditorSource;
    const duration = videoEditorDuration;
    const editorUrl = videoEditorSourceUrl;
    setVideoEditorOpen(false);
    commitVideoFile(source, duration);
    setVideoPreviewFilter('');
    setVideoEditorSource(null);
    setVideoEditorSourceUrl('');
    setVideoEditorDuration(null);
    if (editorUrl) URL.revokeObjectURL(editorUrl);
  };

  const handlePickVideo = (e) => {
    const next = e.target.files?.[0];
    if (next) beginVideoFlow(next);
  };

  const closeVideoRecord = useCallback(() => {
    stopVideoRecordTracks();
    setVideoRecordOpen(false);
    setVideoRecordStarting(false);
    setVideoRecordError('');
  }, [stopVideoRecordTracks]);

  const openVideoRecord = async () => {
    setVideoRecordError('');
    setVideoRecordOpen(true);
    setVideoRecordStarting(true);
    setVideoRecordReady(false);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera recording is not supported in this browser.');
      }
      if (typeof MediaRecorder === 'undefined') {
        throw new Error('Recording is not supported in this browser. You can still upload a video.');
      }
      const stream = await navigator.mediaDevices.getUserMedia(COMMUNITY_VIDEO_CAPTURE_CONSTRAINTS);
      videoRecordStreamRef.current = stream;
      if (videoRecordRef.current) {
        videoRecordRef.current.srcObject = stream;
        await videoRecordRef.current.play().catch(() => {});
      }
      setVideoRecordReady(true);
    } catch (err) {
      setVideoRecordError(
        err?.message ||
          'Could not access the camera/microphone. You can still upload a video from your library.'
      );
    } finally {
      setVideoRecordStarting(false);
    }
  };

  const finishVideoRecording = useCallback(
    async (blob, durationSeconds) => {
      // Cleanup tracks without calling MediaRecorder.stop() again (already stopped).
      stopVideoRecordTracks({ stopRecorder: false });
      setVideoRecordOpen(false);
      setVideoRecordStarting(false);
      setVideoRecordError('');

      if (!blob || blob.size < 1) {
        setError('Recording was empty. Please try again.');
        return;
      }
      const elapsed = Number.isFinite(durationSeconds) && durationSeconds > 0
        ? Math.min(videoMaxSeconds, Math.max(0.5, durationSeconds))
        : videoMaxSeconds;
      const type = blob.type || 'video/webm';
      const baseType = type.split(';')[0].trim().toLowerCase();
      const ext = baseType.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `community-${Date.now()}.${ext}`, {
        type: baseType.includes('mp4') ? 'video/mp4' : 'video/webm',
      });
      // Always pass timer elapsed — MediaRecorder WebM often has Infinity duration.
      await beginVideoFlow(file, elapsed);
    },
    // beginVideoFlow / stopVideoRecordTracks are form-local
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stopVideoRecordTracks]
  );

  const stopVideoRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    try {
      recorder.stop();
    } catch {
      /* ignore */
    }
  }, []);

  const startVideoRecording = () => {
    setVideoRecordError('');
    const stream = videoRecordStreamRef.current;
    if (!stream || !videoRecordReady) {
      setVideoRecordError('Camera is not ready yet.');
      return;
    }
    let recorder;
    let mimeType = '';
    try {
      const capped = createCappedMediaRecorder(stream);
      recorder = capped.recorder;
      mimeType = capped.mimeType;
    } catch (err) {
      setVideoRecordError(err?.message || 'Could not start recording.');
      return;
    }

    recordChunksRef.current = [];
    recordStartedAtRef.current = Date.now();
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordChunksRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      if (recordTimerRef.current) {
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
      }
      setVideoRecording(false);
      const elapsed = (Date.now() - recordStartedAtRef.current) / 1000;
      const blob = new Blob(recordChunksRef.current, {
        type: recorder.mimeType || mimeType || 'video/webm',
      });
      recordChunksRef.current = [];
      finishVideoRecording(blob, elapsed);
    };

    recorder.start(250);
    setVideoRecording(true);
    setVideoRecordElapsed(0);
    recordTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - recordStartedAtRef.current) / 1000;
      setVideoRecordElapsed(elapsed);
      if (elapsed >= videoMaxSeconds) {
        stopVideoRecording();
      }
    }, 200);
  };

  const closeCamera = () => {
    stopCameraStream();
    setCameraOpen(false);
    setCameraError('');
    setCameraStarting(false);
  };

  const openCamera = () => {
    setCameraError('');
    setCameraOpen(true);
  };

  const captureFromCamera = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      setCameraError('Camera is not ready yet. Please wait a moment.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
      setCameraError('Could not capture photo. Please try again.');
      return;
    }
    if (blob.size > POST_IMAGE_MAX_BYTES) {
      setCameraError('Captured image is too large (max 5 MB).');
      return;
    }
    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
    closeCamera();
    beginImageFlow(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed && !imageFile && !videoFile) return;
    setError('');
    const hadMedia = Boolean(imageFile || videoFile);

    // Hand off in-flight color bake to the upload queue — do not block the form.
    const bakePromise = videoBakePromiseRef.current;
    if (bakePromise) {
      bakePromise.handedOff = true;
      videoBakePromiseRef.current = null;
    }

    const handoffVideo = videoFileRef.current || videoFile;
    const handoffPreview = videoPreviewRef.current || videoPreview;

    const handoff = {
      content: trimmed,
      imageFile,
      videoFile: handoffVideo,
      videoPreparePromise: bakePromise || null,
      videoDurationSeconds: videoDuration,
      imagePreviewUrl: imagePreview || null,
      videoPreviewUrl: handoffPreview || null,
      currentUser,
    };

    // Transfer blob URL ownership to the upload queue (revoked on success/error/dismiss).
    if (imagePreview && String(imagePreview).startsWith('blob:')) {
      skipNextImagePreviewRevokeRef.current = true;
    }
    if (handoffPreview && String(handoffPreview).startsWith('blob:')) {
      skipNextVideoPreviewRevokeRef.current = true;
    }

    setImageFile(null);
    setImagePreview('');
    setVideoFile(null);
    setVideoPreview('');
    setVideoDuration(null);
    setVideoPreviewFilter('');
    setOptimizingVideo(false);
    videoFileRef.current = null;
    videoPreviewRef.current = '';
    setContent('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';

    const job = enqueueCommunityPost(handoff);
    onPostCreated(buildOptimisticCommunityPost(job));
    if (hadMedia) {
      setModerationNotice(
        'Your upload was successful. It is currently pending administrator approval. You will be notified once it has been reviewed.'
      );
    }
  };

  const canPost = Boolean(content.trim() || imageFile || videoFile);
  const submitLabel = 'Post';
  const mediaBusy = optimizingImage || optimizingVideo;

  return (
    <Box
      sx={{
        ...panelSx,
        p: { xs: 1.5, sm: 2.5 },
        mb: 0,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 1.5, sm: 2 },
        boxShadow: {
          xs: 'none',
          sm: '0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      }}
      data-testid="create-post-form"
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar
          src={resolveMediaUrl(currentUser?.avatar_url) || undefined}
          alt={currentUser?.name || 'You'}
          sx={{ width: 44, height: 44, bgcolor: 'primary.main', flexShrink: 0, fontWeight: 700, fontSize: 14 }}
        >
          {getInitials(currentUser?.name || currentUser?.first_name)}
        </Avatar>
        <Box component="form" onSubmit={handleSubmit} sx={{ flexGrow: 1, minWidth: 0 }}>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            id="create-post-content"
            placeholder="Share a win, a photo or short clip, or a tip for peers…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            size="small"
            disabled={optimizingImage}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': { bgcolor: (t) => alpha(t.palette.grey[500], 0.04) },
            }}
            aria-label="Post content"
          />

          {imagePreview && (
            <Box
              sx={{
                position: 'relative',
                // Break out of CreatePostForm padding — LinkedIn full-bleed preview
                mx: { xs: -1.5, sm: -2.5 },
                width: { xs: 'calc(100% + 24px)', sm: 'calc(100% + 40px)' },
                mb: 1.5,
                borderRadius: 0,
                overflow: 'hidden',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.900',
                lineHeight: 0,
              }}
            >
              <Box
                component="img"
                src={imagePreview}
                alt="Selected preview"
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  m: 0,
                  p: 0,
                  opacity: mediaBusy ? 0.55 : 1,
                }}
              />
              {mediaBusy && imagePreview && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(15, 23, 42, 0.45)',
                  }}
                  data-testid="post-image-upload-spinner"
                >
                  <CircularProgress size={36} sx={{ color: 'common.white' }} />
                  <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 600 }}>
                    Optimizing photo…
                  </Typography>
                </Stack>
              )}
              {!mediaBusy && (
                <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8 }}>
                  {imageFile?.type !== 'image/gif' && (
                    <Tooltip title="Adjust photo">
                      <IconButton
                        size="small"
                        onClick={() => openImageEditor(imageFile)}
                        aria-label="Adjust photo"
                        disabled={optimizingImage}
                        data-testid="post-image-edit-button"
                        sx={{
                          bgcolor: 'rgba(0,0,0,0.55)',
                          color: 'common.white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                        }}
                      >
                        <Tune fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={clearImage}
                    aria-label="Remove photo"
                    disabled={optimizingImage}
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
              {optimizingImage && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(15, 23, 42, 0.35)' }}
                  data-testid="post-image-optimize-spinner"
                >
                  <CircularProgress size={28} sx={{ color: 'common.white' }} />
                  <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 600 }}>
                    Optimizing photo…
                  </Typography>
                </Stack>
              )}
            </Box>
          )}

          {videoPreview && (
            <Box
              sx={{
                position: 'relative',
                mx: { xs: -1.5, sm: -2.5 },
                width: { xs: 'calc(100% + 24px)', sm: 'calc(100% + 40px)' },
                mb: 1.5,
                borderRadius: 0,
                overflow: 'hidden',
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.900',
                lineHeight: 0,
              }}
              data-testid="post-video-preview"
            >
              <Box
                component="video"
                src={videoPreview}
                controls
                playsInline
                sx={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  mx: 'auto',
                  filter: videoPreviewFilter || 'none',
                  opacity: 1,
                }}
              />
              <Stack direction="row" spacing={0.5} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <Tooltip title="Adjust video">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => openVideoEditor(videoFile, videoDuration)}
                      aria-label="Adjust video"
                      disabled={optimizingVideo || !videoFile}
                      data-testid="post-video-edit-button"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: 'common.white',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                      }}
                    >
                      <Tune fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={clearVideo}
                  aria-label="Remove video"
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              {optimizingVideo && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    right: 8,
                    bottom: 36,
                    zIndex: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: 'rgba(15, 23, 42, 0.72)',
                    pointerEvents: 'none',
                  }}
                  data-testid="post-video-optimize-spinner"
                >
                  <CircularProgress size={16} sx={{ color: 'common.white' }} />
                  <Typography variant="caption" sx={{ color: 'common.white', fontWeight: 600 }}>
                    Applying adjustments… You can Post anytime.
                  </Typography>
                </Stack>
              )}
              {Number.isFinite(videoDuration) && (
                <Typography
                  variant="caption"
                  sx={{
                    position: 'absolute',
                    left: 8,
                    bottom: 8,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.65)',
                    color: 'common.white',
                    fontWeight: 600,
                  }}
                >
                  {videoDuration.toFixed(1)}s / {videoMaxSeconds}s max
                </Typography>
              )}
            </Box>
          )}

          {optimizingImage && !imagePreview && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} data-testid="post-image-optimize-spinner">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Optimizing photo…</Typography>
            </Stack>
          )}
          {optimizingVideo && !videoPreview && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} data-testid="post-video-optimize-spinner">
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">Optimizing video…</Typography>
            </Stack>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {moderationNotice && (
            <Alert
              severity="info"
              sx={{ mb: 1 }}
              onClose={() => setModerationNotice('')}
              data-testid="post-moderation-notice"
            >
              {moderationNotice}
            </Alert>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Stack direction="row" spacing={0.25} alignItems="center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                hidden
                onChange={handlePickImage}
                data-testid="post-image-input"
              />
              <input
                ref={videoInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                hidden
                onChange={handlePickVideo}
                data-testid="post-video-input"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={handlePickImage}
                data-testid="post-camera-input"
              />
              <Tooltip title="Add a photo">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={mediaBusy}
                    aria-label="Add a photo"
                    color={imageFile ? 'primary' : 'default'}
                  >
                    <ImageOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Take a photo">
                <span>
                  <IconButton
                    size="small"
                    onClick={openCamera}
                    disabled={mediaBusy}
                    aria-label="Take a photo"
                    data-testid="post-camera-button"
                  >
                    <PhotoCamera fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Add a short video (≤${videoMaxSeconds}s)`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={mediaBusy}
                    aria-label="Add a short video"
                    color={videoFile ? 'primary' : 'default'}
                    data-testid="post-video-button"
                  >
                    <VideocamOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Record a short video (≤${videoMaxSeconds}s)`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={openVideoRecord}
                    disabled={mediaBusy}
                    aria-label="Record a short video"
                    data-testid="post-video-record-button"
                  >
                    <FiberManualRecord fontSize="small" color="error" />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                {content.length}/2000
              </Typography>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              size="small"
              startIcon={<Send />}
              disabled={!canPost || optimizingImage}
              aria-label="Post"
              sx={{ textTransform: 'none', fontWeight: 700, px: 2.25 }}
            >
              {submitLabel}
            </Button>
          </Stack>
        </Box>
      </Stack>

      <Dialog open={cameraOpen} onClose={closeCamera} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Take a photo</DialogTitle>
        <DialogContent>
          {cameraError && (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setCameraError('')}>
              {cameraError}
            </Alert>
          )}
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.900',
              minHeight: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {cameraStarting && (
              <CircularProgress sx={{ color: 'common.white', position: 'absolute', zIndex: 1 }} />
            )}
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              sx={{
                width: '100%',
                maxHeight: 420,
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                backgroundColor: '#000',
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeCamera} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => {
              closeCamera();
              fileInputRef.current?.click();
            }}
            sx={{ textTransform: 'none' }}
          >
            Choose from library
          </Button>
          <Button
            variant="contained"
            startIcon={<PhotoCamera />}
            onClick={captureFromCamera}
            disabled={cameraStarting || !cameraReady}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      <PostImageEditor
        open={editorOpen}
        sourceUrl={editorSourceUrl}
        applying={editorApplying}
        onClose={handleEditorClose}
        onApply={handleEditorApply}
        onSkip={async () => {
          if (!editorSource || editorApplying) return;
          setEditorApplying(true);
          try {
            setEditorOpen(false);
            await commitProcessedImage(editorSource);
            if (editorSourceUrl) URL.revokeObjectURL(editorSourceUrl);
            setEditorSource(null);
            setEditorSourceUrl('');
          } finally {
            setEditorApplying(false);
          }
        }}
      />

      <Dialog
        open={videoRecordOpen}
        onClose={videoRecording ? undefined : closeVideoRecord}
        maxWidth="sm"
        fullWidth
        data-testid="post-video-record-dialog"
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Record a short video
        </DialogTitle>
        <DialogContent>
          {videoRecordError && (
            <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setVideoRecordError('')}>
              {videoRecordError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Up to {videoMaxSeconds}s with camera + mic. You can adjust brightness afterward.
          </Typography>
          <Box
            sx={{
              position: 'relative',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.900',
              minHeight: 240,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {videoRecordStarting && (
              <CircularProgress sx={{ color: 'common.white', position: 'absolute', zIndex: 1 }} />
            )}
            <Box
              component="video"
              ref={videoRecordRef}
              autoPlay
              playsInline
              muted
              sx={{
                width: '100%',
                maxHeight: 420,
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
                backgroundColor: '#000',
              }}
            />
            {videoRecording && (
              <Chip
                label={`${Math.min(videoMaxSeconds, videoRecordElapsed).toFixed(1)}s / ${videoMaxSeconds}s`}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 12,
                  left: 12,
                  bgcolor: 'error.main',
                  color: 'common.white',
                  fontWeight: 700,
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={closeVideoRecord}
            disabled={videoRecording}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              closeVideoRecord();
              videoInputRef.current?.click();
            }}
            disabled={videoRecording}
            sx={{ textTransform: 'none' }}
          >
            Upload from library
          </Button>
          {!videoRecording ? (
            <Button
              variant="contained"
              color="error"
              startIcon={<FiberManualRecord />}
              onClick={startVideoRecording}
              disabled={videoRecordStarting || !videoRecordReady}
              sx={{ textTransform: 'none', fontWeight: 700 }}
              data-testid="post-video-record-start"
            >
              Start recording
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Stop />}
              onClick={stopVideoRecording}
              sx={{ textTransform: 'none', fontWeight: 700 }}
              data-testid="post-video-record-stop"
            >
              Stop
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <PostVideoEditor
        open={videoEditorOpen}
        sourceUrl={videoEditorSourceUrl}
        maxSeconds={videoMaxSeconds}
        onClose={closeVideoEditor}
        onApply={handleVideoEditorApply}
        onSkip={handleVideoEditorSkip}
      />
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function CommunityFeed({ isDashboardGlimpse = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const composePrefill = searchParams.get('compose') || '';
  const { user: currentUser } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const composeRef = useRef(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.community.getPosts({ limit: isDashboardGlimpse ? 5 : undefined });
      setPosts(res.data.posts || []);
    } catch {
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isDashboardGlimpse]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  useEffect(() => {
    return subscribeCommunityUploads((event) => {
      if (event.type === 'update' && event.job) {
        setPosts((prev) =>
          prev.map((p) => {
            if (String(p.id) !== String(event.job.tempId)) return p;
            return {
              ...p,
              _upload: {
                tempId: event.job.tempId,
                status: event.job.status,
                progress: event.job.progress || 0,
                error: event.job.error || null,
              },
            };
          })
        );
      }
      if (event.type === 'success' && event.job && event.post) {
        setPosts((prev) => {
          const withoutTemp = prev.filter((p) => String(p.id) !== String(event.job.tempId));
          // Avoid duplicates if feed already refreshed
          if (withoutTemp.some((p) => String(p.id) === String(event.post.id))) return withoutTemp;
          return [event.post, ...withoutTemp];
        });
      }
      if (event.type === 'error' && event.job) {
        setPosts((prev) =>
          prev.map((p) => {
            if (String(p.id) !== String(event.job.tempId)) return p;
            return {
              ...p,
              _upload: {
                tempId: event.job.tempId,
                status: 'error',
                progress: event.job.progress || 0,
                error: event.job.error || event.error || 'Upload failed',
              },
            };
          })
        );
      }
      if (event.type === 'removed' && event.tempId) {
        setPosts((prev) =>
          prev.filter((p) => !(String(p.id) === String(event.tempId) && p._upload?.status === 'error'))
        );
      }
    });
  }, []);

  const handlePostCreated = (newPost) => {
    setPosts((prev) => [newPost, ...prev.filter((p) => String(p.id) !== String(newPost.id))]);
  };

  const handlePostDeleted = (postId) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  };

  const handleAuthorClick = (userId) => {
    navigate(`/community/profile/${userId}`);
  };

  const focusCompose = () => {
    composeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const el = document.getElementById('create-post-content');
    if (el) el.focus();
  };

  if (isDashboardGlimpse) {
    return (
      <Box data-testid="community-feed-embedded">
        {currentUser && (
          <Box ref={composeRef} sx={{ mb: 1.25, px: 0 }}>
            <CreatePostForm
              onPostCreated={handlePostCreated}
              currentUser={currentUser}
              initialContent={composePrefill}
            />
          </Box>
        )}
        {loading && (
          <Stack spacing={{ xs: 0, sm: 2 }}>
            {[0, 1, 2].map((i) => (
              <Box key={i} sx={{ ...postCardSx, p: 2 }}>
                <Stack direction="row" spacing={1.5}>
                  <Skeleton variant="circular" width={42} height={42} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="40%" height={18} />
                    <Skeleton width="90%" height={16} sx={{ mt: 1 }} />
                  </Box>
                </Stack>
                <Skeleton variant="rounded" height={140} sx={{ mt: 2, borderRadius: { xs: 0, sm: 2 } }} />
              </Box>
            ))}
          </Stack>
        )}
        {!loading && error && (
          <Alert severity="error" action={<Button size="small" onClick={fetchPosts}>Retry</Button>}>
            {error}
          </Alert>
        )}
        {!loading && !error && posts.length === 0 && (
          <Box sx={{ ...panelSx, p: 3, textAlign: 'center' }}>
            <Typography variant="body1" fontWeight={700} gutterBottom>
              Be the first to share a win
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Post a photo or tip for peers preparing for interviews.
            </Typography>
            {currentUser && (
              <Button variant="contained" size="small" onClick={focusCompose} sx={{ textTransform: 'none', fontWeight: 700 }}>
                Share a win
              </Button>
            )}
          </Box>
        )}
        {!loading && !error && posts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            currentUser={currentUser}
            onAuthorClick={handleAuthorClick}
            onPostDeleted={handlePostDeleted}
            compact
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: (t) =>
          `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.background.default} 160px)`,
        // Clear iOS Safari chrome + floating chat FAB on the last post actions.
        pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 5 },
      }}
      data-testid="community-feed-page"
    >
      <Container maxWidth={false} sx={{ pt: { xs: 0.5, sm: 1 }, pb: { xs: 1.5, sm: 2 }, px: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { md: 'flex-start' },
            gap: { xs: 2, md: 1.25 },
            width: '100%',
          }}
        >
          <Box
            data-testid="community-feed-column"
            sx={{
              flex: { xs: '1 1 auto', md: '1 1 0' },
              width: '100%',
              minWidth: 0,
            }}
          >
            {/* Header lives in the feed column so Shortcuts aligns at the same top */}
            <Box
              sx={{
                px: { xs: SHELL_GUTTER_XS, sm: 0 },
                mb: { xs: 1.25, sm: 1.5 },
              }}
              data-testid="community-feed-header"
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.5 }}
                alignItems={{ sm: 'flex-start' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: -0.4,
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      lineHeight: 1.25,
                    }}
                  >
                    Community
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.35,
                      maxWidth: 520,
                      lineHeight: 1.4,
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    Wins, tips, and photos from people preparing alongside you
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                  <Button
                    component={RouterLink}
                    to="/community/discover"
                    variant="outlined"
                    size="small"
                    startIcon={<PersonSearch />}
                    sx={{ textTransform: 'none', fontWeight: 600, display: { xs: 'none', sm: 'inline-flex' } }}
                  >
                    Find a partner
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RateReview />}
                    onClick={focusCompose}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Share a win
                  </Button>
                </Stack>
              </Stack>
            </Box>

            {currentUser && (
              <Box ref={composeRef} sx={{ px: { xs: SHELL_GUTTER_XS, sm: 0 }, mb: 1.25 }}>
                <CreatePostForm
                  onPostCreated={handlePostCreated}
                  currentUser={currentUser}
                  initialContent={composePrefill}
                />
              </Box>
            )}

            {loading && (
              <Stack spacing={{ xs: 0, sm: 2 }}>
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ ...postCardSx, p: { xs: 2, sm: 2.5 } }}>
                    <Stack direction="row" spacing={1.5}>
                      <Skeleton variant="circular" width={44} height={44} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="40%" height={18} />
                        <Skeleton width="90%" height={16} sx={{ mt: 1 }} />
                        <Skeleton width="70%" height={16} />
                      </Box>
                    </Stack>
                    <Skeleton variant="rounded" height={180} sx={{ mt: 2, borderRadius: { xs: 0, sm: 2 } }} />
                  </Box>
                ))}
              </Stack>
            )}

            {!loading && error && (
              <Alert
                severity="error"
                sx={{ mx: { xs: SHELL_GUTTER_XS, sm: 0 } }}
                action={<Button size="small" onClick={fetchPosts}>Retry</Button>}
              >
                {error}
              </Alert>
            )}

            {!loading && !error && posts.length === 0 && (
              <Box sx={{ ...panelSx, p: { xs: 3, sm: 4.5 }, textAlign: 'center', mx: { xs: SHELL_GUTTER_XS, sm: 0 } }}>
                <EmojiEvents sx={{ fontSize: 40, color: 'primary.light', mb: 1 }} />
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Be the first to share a win
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 400, mx: 'auto' }}>
                  Post a photo from your journey, an offer moment, or a tip that helped you prepare.
                </Typography>
                <Button variant="contained" onClick={focusCompose} sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Share a win
                </Button>
              </Box>
            )}

            {!loading && !error && posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                currentUser={currentUser}
                onAuthorClick={handleAuthorClick}
                onPostDeleted={handlePostDeleted}
              />
            ))}
          </Box>

          <CommunityRightRail hideOpenFullFeed showPractice />
        </Box>
      </Container>
    </Box>
  );
}
